import _ from 'underscore';
import { defineGridPlugin } from '@bingads-webui/projection-grid-plugin';
import dataContextManager from '@bingads-webui-static-legacy/summary/SummaryDataContextManager';
/**
 * @typedef { Object } MenuItem
 * @property { String } text menu item text.
 * @property { String[] } classes classes for the menu item.
 * @property { Function } createView create bulk edit view.
 * @property { MenuItem[] } subItems
 */

/**
 * @typedef { Object } BulkEditOptions
 * @property { Object } status entity status schema.
 * @property { Object } ChangeStatusView entity change status view.
 * @property { Object } ChangeStatusModel entity change status model.
 * @property { String } DeleteConfirmation entity delete confirmation.
 * @property { Object } config config,
 * @property { Object } i18n i18n,
 * @property { String } entityType entity type.
 * @property { Boolean } showDelete show delete option or not. defaults to true.
 * @property { Object } function call back after update toolbar
 * @property { MenuItem[] } items menu items inserted after enable/pause/delete
 * @property { MenuItem[] } frontItems menu items inserted before enable/pause/delete
 */

/**
 * updateGridToolbar
 * @param { Object } gridView gridView plugin
 * @param { Object } toolbar toolbar plugin
 * @param { Object } pagination pagination plugin
 * @param { Object } crossPageSelectAll crossPageSelectAll plugin
 * @param { Object } inlineError inlineError plugin
 * @param { BulkEditOptions } options bulk edit options (details below)
 * @param {object} options.status - status options
 * @param {ChangeStatusView} options.ChangeStatusView - ChangeStatusView
 * @param {ChangeStatusModel} options.ChangeStatusModel - ChangeStatusModel
 * @param {string} options.DeleteConfirmation - Delete Confirmation Message
 * @param {object} options.config - config
 * @param {object} options.filterService - filterService
 * @param {object} options.i18n - i18n
 * @param {string} options.entityType - the entity type
 * @param {function} options.gridDecorationCallback - grid decoration callback
 * @param {boolean} options.showDelete - flag to indicate if Delete option is to be supported
 * @param {boolean} options.showEnable - flag to indicate if Enable option is to be supported
 * @param {boolean} options.showExclude - flag to indicate if Exclude option is to be supported
 * @param {boolean} options.showPause - flag to indicate if Pause option is to be supported
 * @param {boolean} options.showSeparateExclude - indicate Exclude be displayed in separate divider
 * @param {boolean} options.disableDeselectAll - disable deselect all after bulk edit success
 * @returns { void }
 */
function updateGridToolbar(
  gridView,
  toolbar,
  pagination,
  crossPageSelectAll,
  inlineError,
  options
) {
  const menuId = 'dropdown-edit';
  const toolbarEditor = toolbar.gridToolbarGroup.toolbarWithItem(menuId);

  const {
    status,
    ChangeStatusView,
    ChangeStatusModel,
    DeleteConfirmation,
    config,
    filterService,
    i18n,
    entityType,
    items,
    frontItems = [],
    campaignUpgradeItem,
    gridDecorationCallback,
    showDelete = true,
    showEnable = true,
    showExclude = false,
    showPause = true,
    showSeparateExclude = false,
    scope,
    isForVNext = false,
    bulkEditUrl = '',
    customClickHandler = null,
    qualtricsSurveyCallback = null,
    importNotificationCallback = null,
    importBudgetBidNotificationCallback = null,
    disableDeselectAll = false,
  } = options;
  let bulkEditView;

  const defaultBulkEditViewConfig = {
    grid: {
      filterService,
      gridView,
      pgridPlugins: {
        pagination,
        crossPageSelectAll,
      },
      isForVNext,
    },
    onOperationSuccess: () => {
      if (!disableDeselectAll) {
        crossPageSelectAll.deselectAll();
        gridView.deselectAll();
      }
    },
    inlineError,
  };

  function getSelectionInfo() {
    const info = {};

    if (crossPageSelectAll.isAllSelected) {
      info.allPages = true;
      info.selectedRowCount = crossPageSelectAll.viewModel.totalRowsTotalPageCount();
      info.selectedRowIds = null;
    } else {
      const selectedIds = gridView.selectedKeys();

      info.allPages = false;
      info.selectedRowCount = selectedIds.length;
      info.selectedRowIds = selectedIds;
    }
    return info;
  }

  function afterStatusChange(selectionInfo, act) {
    if (selectionInfo.allPages) {
      window.App.trigger(`${entityType}Reload`);
    } else {
      const response = [];

      if (act === status.Deleted) {
        _.each(selectionInfo.selectedRowIds, (rowId) => {
          response.push({ Id: rowId });
        });
        window.App.trigger(`${entityType}Deleted`, { data: response });
      } else {
        let Status = '';

        if (act === status.Paused) {
          Status = status.EnumPaused;
        }
        _.each(selectionInfo.selectedRowIds, (rowId) => {
          response.push({
            Id: rowId,
            Status,
          });
        });
        window.App.trigger(`${entityType}Updated`, { data: response });
      }
    }

    // show import related notifications for bulk edit in campaign related grids
    if (entityType === 'campaign' && act !== status.Deleted && importNotificationCallback && _.isFunction(importNotificationCallback)) {
      const campaignItem = gridView.selectedItems();

      importNotificationCallback({ campaignItemList: campaignItem, updateStatus: act });
    }
  }

  function renderView() {
    if (_.isFunction(bulkEditView.updateSelectedEntityCount)) {
      bulkEditView.listenTo(gridView, 'didSelect', () => {
        const selectInfo = getSelectionInfo();

        if (bulkEditView && bulkEditView.updateSelectedEntityCount) {
          bulkEditView.updateSelectedEntityCount(selectInfo.selectedRowCount);
        }
      });
    }

    bulkEditView.render();
  }

  function removeView() {
    bulkEditView.remove();
    bulkEditView = null;
  }

  function hideToolBar() {
    // use defer here to wait for the this dropdown menu to hide first
    _.defer(() => {
      let editorviewHiden = false;
      const gridContainer = gridView.$el.closest('.bingadsgrid');
      const editorContainer = gridContainer.find('.editor__container');
      const getGridToolbar = () => gridContainer.find('.grid-toolbar');

      if (isForVNext) {
        toolbarEditor.overrideWith(bulkEditView);
      } else {
        editorContainer.html(bulkEditView.$el);
        // show editor
        editorContainer.show();
        bulkEditView.$el.find(':input:enabled:first').focus();
      }

      _.defer(() => {
        // if editor view already hide, then won't hide the toolbar again
        if (editorviewHiden) {
          return;
        }
        // hide the toolbar with empty view, which will have blank height
        // hide using height instaed of calling hide() to work around Edge browser bug,
        // where browser tab crashes when used with narrator.
        const gridToolbar = getGridToolbar();
        gridToolbar.css({
          height: 0,
          overflow: 'hidden',
        });
        gridToolbar.attr('aria-hidden', 'true');

        toolbar.gridToolbarGroup.hideAllToolbars();
      });

      bulkEditView.once('hide', () => {
        editorviewHiden = true;

        const gridToolbar = getGridToolbar();
        gridToolbar.css({
          height: 'auto',
          overflow: 'visible',
        });
        gridToolbar.attr('aria-hidden', 'false');
        editorContainer.hide();
        toolbar.gridToolbarGroup.showAllToolbars();
        removeView();

        if (isForVNext) {
          toolbarEditor.overrideWith(null);
        }
      });
    });
  }

  defaultBulkEditViewConfig.hideToolBar = hideToolBar;

  function noneSelectedAndAlert(count) {
    if (count < 1) {
      // eslint-disable-next-line no-alert, no-restricted-globals
      alert(i18n.getString('Grid_RowSelectionRequired'));
      return true;
    }

    return false;
  }

  function changeStatus(act) {
    const info = getSelectionInfo();

    if (noneSelectedAndAlert(info.selectedRowCount)) {
      return;
    }

    if (!crossPageSelectAll.isAllSelected) {
      if (_.isFunction(customClickHandler)) {
        const keepOriginal = !customClickHandler(crossPageSelectAll, gridView, i18n, act);
        if (!keepOriginal) {
          return;
        }
      }
      // eslint-disable-next-line no-alert, no-restricted-globals
      if (act === status.Deleted && !confirm(DeleteConfirmation)) {
        return;
      }
    }

    // fire qualtrics event if user pauses all campaigns on the page or pauses all their campaigns.
    if (entityType === 'campaign' &&
        qualtricsSurveyCallback &&
        (crossPageSelectAll.isAllSelected ||
          crossPageSelectAll.viewModel.currentPageAllRowsSelected()
        ) &&
        act === status.Paused) {
      qualtricsSurveyCallback();
    }

    const statusModel = new ChangeStatusModel({
      selectedEntityCount: info.selectedRowCount,
      allPages: info.allPages,
      scope,
    });
    const action = statusModel.get('BatchAction').Actions[0];

    action.Value = act;

    bulkEditView = new ChangeStatusView(_.defaults({
      model: statusModel,
      onOperationSuccess: () => {
        afterStatusChange(info, act);
        if (!disableDeselectAll) {
          crossPageSelectAll.deselectAll();
          gridView.deselectAll();
        }
      },
      isSkipEntityCache: act === status.Deleted,
      isForVNext,
      bulkEditUrl,
    }, defaultBulkEditViewConfig));
    renderView();

    if (!isForVNext
      || crossPageSelectAll.isAllSelected
      || (typeof bulkEditView.isExcludeNonAudience === 'function' && bulkEditView.isExcludeNonAudience())) {
      // For VNext only hide
      //   1. when cross-page is selected
      //   2. when non audience campaign target is excluded.
      // Since that the only time we show top of grid panel for change status.
      hideToolBar();
    }
  }

  function generateClickableItem({
    text,
    iconLeft,
    iconLeftGlyphName,
    classes,
    createView,
    isFallbackEnabled,
    noSelectionRequired = false,
    hotkeys,
  }) {
    return {
      text,
      iconLeft,
      iconLeftGlyphName,
      classes,
      isFallbackEnabled,
      onClick: isFallbackEnabled ? null : () => {
        const info = getSelectionInfo();

        // check the selection for the menu that do require selection
        if (!noSelectionRequired && noneSelectedAndAlert(info.selectedRowCount)) {
          return;
        }
        if (_.isFunction(customClickHandler)) {
          const keepOriginal = !customClickHandler(crossPageSelectAll, gridView, i18n);
          if (!keepOriginal) {
            return;
          }
        }
        const selectedItems = gridView.selectedItems();

        const viewConfig = {
          ...defaultBulkEditViewConfig,
          onOperationSuccess: (model) => {
            const newValue = _.get(model, ['attributes', 'BatchAction', 'Actions', 0]);
            if (newValue && importBudgetBidNotificationCallback) {
              if (classes[0] === 'bulk-edit-change-budget') {
                importBudgetBidNotificationCallback({ campaignItemList: selectedItems, newBudget: newValue });
              }
              if (classes[0] === 'bulk-edit-change-lifetime-budget') {
                importBudgetBidNotificationCallback({ campaignItemList: selectedItems, newBudget: newValue });
              }
              if (classes[0] === 'bulk-edit-change-bid-strategy-type') {
                importBudgetBidNotificationCallback({ campaignItemList: selectedItems, newBidStrategy: newValue });
              }
            }

            defaultBulkEditViewConfig.onOperationSuccess();
          },
        };

        bulkEditView = createView({
          viewConfig,
          selectedRowCount: info.selectedRowCount,
          allPages: info.allPages,
          selectedRowIds: info.selectedRowIds,
          isForVNext,
          selectedItems,
        });

        if (_.isUndefined(bulkEditView)) {
          return;
        }

        bulkEditView.once('close', () => {
          toolbar.gridToolbarGroup.showAllToolbars();
          dataContextManager.updateContext(['AdGrid']);
          gridView.refresh(true);
        });

        bulkEditView.once('saveAndRefresh', () => {
          toolbar.gridToolbarGroup.showAllToolbars();
          dataContextManager.updateContext(['AdGrid']);
          gridView.refresh(true);
        });

        renderView();
        hideToolBar();
      },
      hotkeys,
    };
  }

  function generateClickableCampaignUpgradeItem({
    text,
    iconLeft,
    iconLeftGlyphName,
    classes,
    repos,
    isFallbackEnabled,
    noSelectionRequired = false,
    hotkeys,
  }) {
    return {
      text,
      iconLeft,
      iconLeftGlyphName,
      classes,
      isFallbackEnabled,
      onClick: isFallbackEnabled ? null : () => {
        const info = getSelectionInfo();

        // check the selection for the menu that do require selection
        if (!noSelectionRequired && noneSelectedAndAlert(info.selectedRowCount)) {
          return;
        }
        const selectedItems = gridView.selectedItems();
        const { allPages } = info;
        repos.openUpgradeDialog(selectedItems, allPages);
      },
      hotkeys,
    };
  }

  function getItems() {
    function getIconClasses(cls) {
      if (isForVNext) {
        return [cls];
      }
      return ['glyphicon', 'glyphicon-offset-left5'].concat(cls);
    }

    const divider = {
      type: 'dropdown-divider',
    };

    const excludeItem = {
      text: i18n.getString('Page_All_RowactionText_Exclude'),
      iconLeft: getIconClasses('icon-status-exclude'),
      iconLeftGlyphName: 'ba-Blocked',
      classes: ['bulk-edit-exclude'],
      onClick: () => {
        changeStatus(status.Excluded);
      },
    };

    const menuItems = _.compact([
      showEnable && {
        text: i18n.getString('Page_All_RowactionText_Enable'),
        iconLeft: getIconClasses('icon-status-active'),
        iconLeftGlyphName: 'ba-RadioBullet2',
        classes: ['bulk-edit-enable'],
        onClick: () => {
          changeStatus(status.Active);
        },
      },
      showPause && {
        text: i18n.getString('Page_All_RowactionText_Pause'),
        iconLeft: getIconClasses('icon-status-pause'),
        iconLeftGlyphName: 'ba-PauseListView12',
        classes: ['bulk-edit-pause'],
        onClick: () => {
          changeStatus(status.Paused);
        },
      },
      (showExclude && !showSeparateExclude) && excludeItem,
    ]);

    if (showDelete) {
      menuItems.splice(menuItems.length, 0, {
        text: i18n.getString('Page_All_RowactionText_Delete'),
        iconLeft: getIconClasses('icon-status-delete'),
        iconLeftGlyphName: 'ba-Delete',
        classes: ['bulk-edit-delete'],
        onClick: () => {
          changeStatus(status.Deleted);
        },
      }, divider);
    }

    if (showExclude && showSeparateExclude) {
      menuItems.splice(menuItems.length, 0, excludeItem, divider);
    }

    // Add a divider if not exist, before next group
    if (menuItems.lastIndexOf(divider) !== menuItems.length - 1) {
      menuItems.push(divider);
    }

    _.each(items, (item) => {
      if (_.isUndefined(item.isValid) || item.isValid()) {
        if (item.subItems) {
          menuItems.push({
            type: 'dropdown-submenu',
            menu: {
              items: _.map(item.subItems, subItem => generateClickableItem(subItem)),
            },
            button: {
              text: item.text,
              iconRight: ['glyphicon', 'toolbar-icon', 'grid-expand-icon', 'pull-right'],
            },
          });
        } else if (item.type === divider.type) {
          menuItems.push(item);
        } else {
          menuItems.push(generateClickableItem(item));
        }
      }
    });
    if (_.result(campaignUpgradeItem, 'isValid')) {
      const { allPages: allPagesSelected } = getSelectionInfo();
      const curSelectedItems = gridView.selectedItems();
      if (allPagesSelected || _.any(curSelectedItems, item => item.CampaignType === 'Shopping' && item.CampaignSubType === 'ShoppingSmartAds')) {
        menuItems.push(generateClickableCampaignUpgradeItem(campaignUpgradeItem));
      }
    }
    const frontMenuItems = [];
    _.each(frontItems, (item) => {
      if (_.isUndefined(item.isValid) || item.isValid()) {
        frontMenuItems.push(generateClickableItem(item));
      }
    });
    if (!_.isEmpty(frontMenuItems)) {
      frontMenuItems.push(divider);
    }

    return frontMenuItems.concat(menuItems);
  }

  function updateToolbar() {
    const isReadOnlyUser = config.get('Permissions').IsReadOnlyUser;
    const isAccountInactive = config.get('CurrentAccountInActive');

    toolbarEditor.update({
      type: 'dropdown',
      id: 'dropdown-edit',
      iconLeftGlyphName: 'ba-Edit',
      button: {
        text: i18n.getString('Grid_EditButtonText'),
        disabled: isReadOnlyUser || isAccountInactive,
        classes: ['btn', 'btn-secondary'],
      },
      menu: {
        items: getItems(),
      },
    });
  }

  gridView.on('didSelect', () => {
    if (isForVNext && !_.isEmpty(gridView.selectedItems())) {
      updateToolbar();
    }
  });

  updateToolbar();

  // For some of the grid, we need to show some message
  if (_.isFunction(gridDecorationCallback)) {
    gridDecorationCallback(gridView);
  }
}

/**
 * Projection grid bulk edit plugin,
 */
export const projectionGridBulkEdit = defineGridPlugin(
  'projectionGridBulkEdit', [
    'gridView',
    'toolbar',
    'pagination',
    'crossPageSelectAll',
    'inlineError',
  ],
  updateGridToolbar
);
